import {
  ClinicSuggestionResponseDto,
  CommonEntitiesListResponse,
  SaasClinicResponseDto,
} from '@docbay/schemas';
import {
  GetClinicsParams,
  UpdateClinicManagementData,
} from 'common/types/clinicsManagement';
import axiosInstance from './axiosInstance';

/**
 * A collection of API methods for managing clinics.
 */
export const ClinicsManagementAPI = {
  /**
   * Retrieves a list of clinics based on the specified parameters.
   *
   * @param {GetClinicsParams} params - An object containing query parameters for filtering and sorting the clinics.
   * @returns {Promise<CommonEntitiesListResponse<SaasClinicResponseDto>>} A promise that resolves to an object containing the list of clinics.
   */
  getClinics: (params: GetClinicsParams) =>
    axiosInstance.get<CommonEntitiesListResponse<SaasClinicResponseDto>>(
      '/saas/clinics-management',
      {
        params,
      },
    ),

  /**
   * Retrieves a list of clinic suggestions based on the specified search term.
   *
   * @param {string} search - The search term to use for filtering the clinics.
   * @returns {Promise<ClinicSuggestionResponseDto[]>} A promise that resolves to an array of clinic suggestion objects.
   */
  getClinicsSuggestions: (search: string) =>
    axiosInstance.get<ClinicSuggestionResponseDto[]>(
      '/saas/clinics-management/suggestions',
      { params: { search } },
    ),

  /**
   * Retrieves a single clinic by its ID.
   *
   * @param {string} clinicId - The unique identifier for the clinic to retrieve.
   * @returns {Promise<SaasClinicResponseDto>} A promise that resolves to the retrieved clinic object.
   */
  getClinicById: (clinicId: string) =>
    axiosInstance.get<SaasClinicResponseDto>(
      `/saas/clinics-management/${clinicId}`,
    ),

  /**
   * Updates the billing API key for a single clinic.
   *
   * @param {UpdateClinicManagementData} data - An object containing the clinic ID and new billing API key to update.
   * @returns {Promise<void>} A promise that resolves when the update is complete.
   */
  updateClinic: (data: UpdateClinicManagementData) => {
    const { clinicId, billingApiKey } = data;
    return axiosInstance.put(
      `/saas/clinics-management/${clinicId}/billing-api-key`,
      {
        billingApiKey,
      },
    );
  },

  /**
   * Deletes the billing API key for a single clinic.
   *
   * @param {string} clinicId - The unique identifier for the clinic to delete.
   * @returns {Promise<void>} A promise that resolves when the deletion is complete.
   */
  deleteClinic: (clinicId: string) =>
    axiosInstance.delete(
      `/saas/clinics-management/${clinicId}/billing-api-key`,
    ),
};
